import { useCallback } from "react"
import type { WhatsappChat } from "@stockline/shared/types/communications/whatsapp-chat"
import { WHATSAPP_CHAT_TABLE } from "../../../lib/offline/resources/communications/whatsapp-chat/db"
import { useAISync } from "../../ai/providers/AISyncProvider"
import type { OptimisticRollback } from "./useSmsChatMutations"

/**
 * Whitelist of fields the V2 push pipeline accepts on update.
 * Mirrors the server-side `WhatsappChatUpdateFields` interface — keep in sync.
 */
export interface UpdateWhatsappChatInput {
  name?: string
  aiContext?: string | null
  aiAnalysisEnabled?: boolean
}

export function useWhatsappChatMutations() {
  const { storage, changeQueue, engine } = useAISync()

  const updateChat = useCallback(
    async (id: string, input: UpdateWhatsappChatInput): Promise<void> => {
      const existing = await storage.get<WhatsappChat>(WHATSAPP_CHAT_TABLE, id)
      if (!existing) return

      const updated: WhatsappChat = {
        ...existing,
        ...input,
        updatedAt: new Date().toISOString(),
      }

      await storage.put(WHATSAPP_CHAT_TABLE, updated)
      await changeQueue.enqueue("whatsapp-chats", {
        op: "update",
        id,
        data: input,
      })
      void engine.pushChanges("whatsapp-chats")
    },
    [storage, changeQueue, engine]
  )

  // Optimistically clears `unreadCount` in Dexie and returns a rollback
  // closure. `unreadCount` is not part of the V2 push whitelist — the
  // canonical write still happens against the legacy backend, which bumps
  // sync_id and fans out `whatsapp-chat.updated` so Dexie reconciles on the
  // SSE round-trip. Pure local optimism keeps the chat list at zero
  // instantly when the user opens a chat.
  const markAsReadOptimistic = useCallback(
    async (id: string): Promise<OptimisticRollback | null> => {
      const existing = await storage.get<WhatsappChat>(WHATSAPP_CHAT_TABLE, id)
      if (!existing || existing.unreadCount === 0) return null

      const previous = existing
      await storage.put<WhatsappChat>(WHATSAPP_CHAT_TABLE, {
        ...existing,
        unreadCount: 0,
      })

      return async () => {
        const current = await storage.get<WhatsappChat>(WHATSAPP_CHAT_TABLE, id)
        // Skip rollback if the row is gone (deleted by a newer SSE event —
        // restoring `previous` would resurrect it) or if SSE has already
        // advanced syncId past `previous`. Strict `<` so a same-syncId SSE
        // write between optimistic update and error isn't clobbered.
        if (current && (current.syncId ?? 0) < (previous.syncId ?? 0)) {
          await storage.put<WhatsappChat>(WHATSAPP_CHAT_TABLE, previous)
        }
      }
    },
    [storage]
  )

  // Bulk variant of `markAsReadOptimistic` — flips every Dexie row's
  // `unreadCount` to 0 in one `putMany`. The chat list reads from Dexie via
  // `useWhatsappChats`, so without this the unread badges stay visible
  // until the per-chat `whatsapp-chat.updated` SSE events arrive (one
  // round-trip per chat, choppy on large lists). The legacy backend's
  // bulk endpoint stays the canonical writer; the per-row SSE events still
  // arrive and reconcile syncId — this just lights up the UI instantly.
  const markAllAsReadOptimistic =
    useCallback(async (): Promise<OptimisticRollback | null> => {
      try {
        const all = await storage.getAll<WhatsappChat>(WHATSAPP_CHAT_TABLE)
        const targets = all.filter((c) => !c.deletedAt && c.unreadCount > 0)
        if (targets.length === 0) return null

        const previousById = new Map<
          string,
          { unreadCount: number; syncId: number }
        >()
        for (const c of targets) {
          previousById.set(c.id, {
            unreadCount: c.unreadCount,
            syncId: c.syncId ?? 0,
          })
        }

        await storage.putMany<WhatsappChat>(
          WHATSAPP_CHAT_TABLE,
          targets.map((c) => ({ ...c, unreadCount: 0 }))
        )

        return async () => {
          try {
            const fresh =
              await storage.getAll<WhatsappChat>(WHATSAPP_CHAT_TABLE)
            const restored: WhatsappChat[] = []
            for (const c of fresh) {
              const prev = previousById.get(c.id)
              if (!prev) continue
              // Strict `<`: a newer SSE write (higher syncId) is
              // authoritative, leave it alone. Same-syncId rows are still
              // ours to restore — no server delta has landed.
              if (prev.syncId < (c.syncId ?? 0)) continue
              restored.push({ ...c, unreadCount: prev.unreadCount })
            }
            if (restored.length > 0) {
              await storage.putMany<WhatsappChat>(WHATSAPP_CHAT_TABLE, restored)
            }
          } catch (error) {
            console.warn("markAllAsReadOptimistic rollback failed", error)
          }
        }
      } catch (error) {
        console.warn("markAllAsReadOptimistic failed", error)
        return null
      }
    }, [storage])

  // Optimistically flip `archived` in Dexie. Same rationale as
  // SMS — the legacy FastAPI archive route owns the write and emits
  // `whatsapp-chat.updated` via RabbitMQ → SSE, but the chat list reads
  // from Dexie so without this the UI doesn't react until SSE arrives.
  const setArchivedOptimistic = useCallback(
    async (
      id: string,
      archived: boolean
    ): Promise<OptimisticRollback | null> => {
      const existing = await storage.get<WhatsappChat>(WHATSAPP_CHAT_TABLE, id)
      if (!existing || existing.archived === archived) return null

      const previousArchived = existing.archived
      const previousSyncId = existing.syncId ?? 0
      await storage.put<WhatsappChat>(WHATSAPP_CHAT_TABLE, {
        ...existing,
        archived,
      })

      return async () => {
        const current = await storage.get<WhatsappChat>(WHATSAPP_CHAT_TABLE, id)
        if (!current) return
        if (previousSyncId < (current.syncId ?? 0)) return
        await storage.put<WhatsappChat>(WHATSAPP_CHAT_TABLE, {
          ...current,
          archived: previousArchived,
        })
      }
    },
    [storage]
  )

  // Optimistically flip `enabled` in Dexie (chat disable / re-enable).
  // Message tombstones from the cascade arrive separately via
  // `whatsapp-message.deleted` events.
  const setEnabledOptimistic = useCallback(
    async (
      id: string,
      enabled: boolean
    ): Promise<OptimisticRollback | null> => {
      const existing = await storage.get<WhatsappChat>(WHATSAPP_CHAT_TABLE, id)
      if (!existing || existing.enabled === enabled) return null

      const previousEnabled = existing.enabled
      const previousSyncId = existing.syncId ?? 0
      await storage.put<WhatsappChat>(WHATSAPP_CHAT_TABLE, {
        ...existing,
        enabled,
      })

      return async () => {
        const current = await storage.get<WhatsappChat>(WHATSAPP_CHAT_TABLE, id)
        if (!current) return
        if (previousSyncId < (current.syncId ?? 0)) return
        await storage.put<WhatsappChat>(WHATSAPP_CHAT_TABLE, {
          ...current,
          enabled: previousEnabled,
        })
      }
    },
    [storage]
  )

  // Optimistically flip `groupId` in Dexie (chat sharing toggle).
  // See SMS mutations for rationale — group sharing triggers fan-out
  // and `dedupeChatsByConversationKey` only collapses seats once
  // Dexie reflects the new group_id.
  const setGroupOptimistic = useCallback(
    async (
      id: string,
      groupId: string | null
    ): Promise<OptimisticRollback | null> => {
      const existing = await storage.get<WhatsappChat>(WHATSAPP_CHAT_TABLE, id)
      if (!existing || existing.groupId === groupId) return null

      const previousGroupId = existing.groupId
      const previousSyncId = existing.syncId ?? 0
      await storage.put<WhatsappChat>(WHATSAPP_CHAT_TABLE, {
        ...existing,
        groupId,
      })

      return async () => {
        const current = await storage.get<WhatsappChat>(WHATSAPP_CHAT_TABLE, id)
        if (!current) return
        if (previousSyncId < (current.syncId ?? 0)) return
        await storage.put<WhatsappChat>(WHATSAPP_CHAT_TABLE, {
          ...current,
          groupId: previousGroupId,
        })
      }
    },
    [storage]
  )

  // Optimistically flip `aiAnalysisEnabled` in Dexie AND enqueue the push.
  // Unlike `enabled` (legacy route), this field is fully V2-managed.
  const setAiAnalysisEnabledOptimistic = useCallback(
    async (
      id: string,
      aiAnalysisEnabled: boolean
    ): Promise<OptimisticRollback | null> => {
      const existing = await storage.get<WhatsappChat>(WHATSAPP_CHAT_TABLE, id)
      if (!existing || existing.aiAnalysisEnabled === aiAnalysisEnabled)
        return null

      const previousValue = existing.aiAnalysisEnabled
      const previousSyncId = existing.syncId ?? 0
      await storage.put<WhatsappChat>(WHATSAPP_CHAT_TABLE, {
        ...existing,
        aiAnalysisEnabled,
      })
      await changeQueue.enqueue("whatsapp-chats", {
        op: "update",
        id,
        data: { aiAnalysisEnabled },
      })
      void engine.pushChanges("whatsapp-chats")

      return async () => {
        const current = await storage.get<WhatsappChat>(WHATSAPP_CHAT_TABLE, id)
        if (!current) return
        if (previousSyncId < (current.syncId ?? 0)) return
        await storage.put<WhatsappChat>(WHATSAPP_CHAT_TABLE, {
          ...current,
          aiAnalysisEnabled: previousValue,
        })
      }
    },
    [storage, changeQueue, engine]
  )

  return {
    updateChat,
    markAsReadOptimistic,
    markAllAsReadOptimistic,
    setArchivedOptimistic,
    setEnabledOptimistic,
    setGroupOptimistic,
    setAiAnalysisEnabledOptimistic,
  }
}
